import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { MDBBtn, MDBIcon } from 'mdb-react-ui-kit'
import CustomAlert from './../../shared/CustomAlert'
import LoadingSpinner from './../../shared/LoadingSpinner.component'
import LeadPackageCard from './../../../pages/OrderLeads/components/LeadPackageCard.component'
import { TransferSourceLeadVendor, TransferAmount } from './../../ledgers/TransfersBar/TransfersBar.component'
import TransferStore from './../../ledgers/store/Transfer.store'
import { convertToCurrency } from './../../../shared/formatters/convertToCurrency.formatter'
import { convertToCurrencyWithoutSign } from './../../../shared/formatters/convertToCurrencyWithoutSign.formatter'
import Store from './OnboardingLeads.store'

import './OnboardingLeadsPanel.scss'

const TRANSFER_NOTICE = 'It can take 24-48 hours for transferred BUCK$ to appear in your lead vendor account.'

const TransferChecks = observer(() => {
  const amount = parseInt(TransferStore.transfer.amount),
    minimum = parseInt(TransferStore.minimumRequired),
    vendorName = TransferStore.LeadVendors.find(
      Vendor => `${Vendor.id}` === `${TransferStore.transfer.lead_vendor_id}`
    )?.name,
    isBelowMinimum = amount > 0 && minimum > 0 && amount < minimum,
    shortfall = amount > 0 ? amount - Store.balance : 0

  return (
    <>
      {isBelowMinimum ? (
        <CustomAlert color='warning' className='olp-check'>
          Minimum order for {vendorName} is {convertToCurrencyWithoutSign(minimum)} BUCK$.
        </CustomAlert>
      ) : null}
      {shortfall > 0 ? (
        <CustomAlert color='warning' className='olp-check olp-shortfall'>
          <span>
            You have {convertToCurrencyWithoutSign(Store.balance)} BUCK$. This order needs{' '}
            {convertToCurrencyWithoutSign(amount)}. Add {convertToCurrencyWithoutSign(shortfall)} more.
          </span>
          <MDBBtn type='button' color='indigo' size='sm' disabled={Store.isBuying} onClick={() => Store.buyBucks()}>
            <MDBIcon icon='plus-circle' />
            &nbsp;Add BUCK$
          </MDBBtn>
        </CustomAlert>
      ) : null}
    </>
  )
})

const TransferOutcome = observer(() => {
  if (!TransferStore.outcomeMsg) return null

  if (TransferStore.showSuccessMsg)
    return (
      <CustomAlert color='success' className='olp-outcome'>
        <div>{TransferStore.outcomeMsg}</div>
        <small>{TRANSFER_NOTICE}</small>
      </CustomAlert>
    )

  if (TransferStore.showErrorMsg)
    return (
      <CustomAlert color='danger' className='olp-outcome'>
        {TransferStore.outcomeMsg}
      </CustomAlert>
    )

  return null
})

const LeadTransferForm = observer(() => {
  const amount = parseInt(TransferStore.transfer.amount),
    minimum = parseInt(TransferStore.minimumRequired),
    isBlocked = amount > 0 && ((minimum > 0 && amount < minimum) || amount > Store.balance)

  useEffect(() => {
    Store.transferFormMounted()
  }, [])

  return (
    <form
      className='olp-transfer'
      noValidate
      onSubmit={evt => {
        evt.preventDefault()
        Store.submitTransfer()
        return false
      }}
    >
      <h5>Transfer BUCK$ to a lead vendor</h5>
      <TransferSourceLeadVendor required={true} />
      <TransferAmount required={true} />
      <TransferChecks />
      <TransferOutcome />
      <MDBBtn
        type='submit'
        color='indigo'
        block
        className='olp-submit'
        disabled={TransferStore.isDisabled || isBlocked || Store.isBuying}
      >
        {TransferStore.isSaving ? (
          <>
            <i className='fa fa-spin fa-spinner'></i>&nbsp;Transferring BUCK$
          </>
        ) : (
          'Transfer BUCK$ to Lead Vendor'
        )}
      </MDBBtn>
    </form>
  )
})

const OnboardingLeadsPanel = () => {
  useEffect(() => Store.watchEdits(), [])

  if (Store.isLoading && !Store.isLoaded)
    return (
      <div className='OnboardingLeadsPanelComponent is-loading'>
        <LoadingSpinner size='md' isActive={true} />
      </div>
    )

  if (Store.feedFailed && !Store.Packages.length)
    return (
      <div className='OnboardingLeadsPanelComponent'>
        <CustomAlert color='warning' className='olp-feed-error'>
          We couldn't load lead packages right now.
        </CustomAlert>
      </div>
    )

  return (
    <div className='OnboardingLeadsPanelComponent'>
      <h4>Put your BUCK$ to work!</h4>
      <div className='olp-balance'>
        Your USABG BUCK$ balance: <strong>{convertToCurrency(Store.balance)}</strong>
      </div>
      <p className='olp-explainer'>
        USABG BUCK$ are credits you can use to buy leads. You earn them through your sales and through the weekly
        bonuses awarded at the National Sales Meeting. Choose a lead vendor below and transfer your BUCK$ to get your
        first leads on the way.
      </p>
      {Store.Packages.length ? (
        <div className='olp-packages'>
          {Store.Packages.map(Package => (
            <LeadPackageCard key={`olp-pkg-${Package.id()}`} Package={Package} compact />
          ))}
        </div>
      ) : null}
      {Store.Ledger ? <LeadTransferForm /> : null}
    </div>
  )
}

export default observer(OnboardingLeadsPanel)
